import type { RoomLayoutSpot, RoomLayoutSpotTypeItem } from './RoomLayoutMap.types';
import { SpotStatus } from './RoomLayoutMap.types';
import { hasSpotTypeName, hasSpotTypePrefix } from './spotTypeGuards';

/** One spot-type row in the legend: tag (e.g. "BF") and label (e.g. "Bikeformer"). */
export type SpotTypeLegendItem = {
  tag: string;
  label: string;
};

/** BOOKABLE and BLOCKED spots participate in the equipment legend; NOT_BOOKABLE (e.g. trainer) does not. */
function isSpotEligibleForTypeLegend(spot: RoomLayoutSpot): boolean {
  return spot.status === SpotStatus.BOOKABLE || spot.status === SpotStatus.BLOCKED;
}

function spotTypeItemsFromLayoutTypes(spotTypes: RoomLayoutSpotTypeItem[]): SpotTypeLegendItem[] {
  return spotTypes.map(({ name, prefix }) => ({ tag: prefix.trim(), label: name }));
}

/**
 * Keep layout-level equipment rows only when that type appears on at least one bookable/blocked spot
 * (matches name; if the spot has a prefix, it must match too).
 *
 * When the basic room-layout response has `spot_types` but spots are not enriched with `spot_type_name`,
 * name-based filtering yields no rows. In that case we show all layout `spot_types` (same as mapping
 * `spot_types` directly), so the equipment legend is not replaced by an empty list.
 */
export function spotTypeLegendItemsFromSpotTypesForBookableSpots(
  spotTypes: RoomLayoutSpotTypeItem[],
  spots: RoomLayoutSpot[]
): SpotTypeLegendItem[] {
  const matched = spotTypes
    .filter((spotTypeItem) =>
      spots.some((spot) => spotTypeItemMatchesEligibleSpot(spotTypeItem, spot))
    )
    .map(({ name, prefix }) => ({ tag: prefix.trim(), label: name }));

  if (matched.length > 0) return matched;

  const anyEligibleHasSpotTypeName = spots.some(
    (spot) => isSpotEligibleForTypeLegend(spot) && hasSpotTypeName(spot)
  );
  if (!anyEligibleHasSpotTypeName && spotTypes.length > 0) {
    return spotTypeItemsFromLayoutTypes(spotTypes);
  }

  return matched;
}

function spotTypeItemMatchesEligibleSpot(
  spotTypeItem: RoomLayoutSpotTypeItem,
  spot: RoomLayoutSpot
): boolean {
  if (!isSpotEligibleForTypeLegend(spot) || !hasSpotTypeName(spot)) return false;
  if (spot.spot_type_name !== spotTypeItem.name) return false;
  if (hasSpotTypePrefix(spot)) return spot.spot_type_prefix === spotTypeItem.prefix;
  return true;
}

/** Spot has at least one of spot_type_name or spot_type_prefix as a non-empty string. */
function hasEnrichedSpotType(spot: {
  spot_type_name?: unknown;
  spot_type_prefix?: unknown;
}): boolean {
  return hasSpotTypeName(spot) || hasSpotTypePrefix(spot);
}

/**
 * Derive legend spot-type items from spots when API provides spot_type_name or spot_type_prefix.
 * Returns undefined when no spot has enriched spot-type data (so UI can fall back to defaults).
 */
export function deriveSpotTypeLegendItemsFromSpots(
  spots: RoomLayoutSpot[]
): SpotTypeLegendItem[] | undefined {
  const legendSpots = spots.filter(isSpotEligibleForTypeLegend);
  const hasAnyEnrichedSpotType = legendSpots.some(hasEnrichedSpotType);
  if (!hasAnyEnrichedSpotType) return undefined;

  const byId = new Map<string, { name: string; prefix: string }>();
  for (const s of legendSpots) {
    if (!s.spot_type_id) continue;
    const hasName = hasSpotTypeName(s);
    const hasPrefix = hasSpotTypePrefix(s);
    const name = hasName ? s.spot_type_name : s.spot_type_id;
    const prefix = (hasPrefix ? s.spot_type_prefix : s.spot_type_id.slice(0, 2)).trim();
    const existing = byId.get(s.spot_type_id);
    if (!existing) {
      byId.set(s.spot_type_id, { name, prefix });
    } else {
      byId.set(s.spot_type_id, {
        name: hasName ? s.spot_type_name : existing.name,
        prefix: hasPrefix ? s.spot_type_prefix : existing.prefix
      });
    }
  }
  return Array.from(byId.entries()).map(([, { name, prefix }]) => ({
    tag: prefix.trim(),
    label: name
  }));
}
